// app/src/lib/revenue/vendor1099.js — 1099 vendor tracking with encrypted TIN (US-4.7).
//
// Security:
//   - TIN is NEVER stored in plaintext. Each TIN is AES-256-GCM-encrypted with the
//     HKDF-derived `piiKey` and stored as a base64url envelope. The plaintext is
//     produced only on demand for the year-end CSV export and never written to disk.
//   - YTD-paid totals exclude duplicate / disputed invoices.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { deriveKeys, encrypt, decrypt, b64uEncode, b64uDecode } from '../crypto.js';
import * as kv from '../idbKv.js';

const THRESHOLD_CENTS = 600_00; // US default $600/year.

/** @typedef {{ taxInfoId:string, userId:string, vendorId:string, taxYear:number, needs1099:0|1, tinCiphertextB64u?:string, totalPaidYtdCents:number, lastRecomputedAt:number }} VendorTaxInfo */

/** Encrypt the supplied TIN string with the user's PII key. */
async function encryptTin(plain) {
  const claims = await getClaimsSafe();
  if (!claims) throw new Error('tin: not signed in');
  const { piiKey } = await deriveKeys(claims.sub, kv);
  const aad = new TextEncoder().encode(`kanjo/pii/v1|tin|${claims.sub}`);
  const env = await encrypt(piiKey, new TextEncoder().encode(plain), aad);
  return b64uEncode(env);
}

async function decryptTin(b64u) {
  const claims = await getClaimsSafe();
  if (!claims) throw new Error('tin: not signed in');
  const { piiKey } = await deriveKeys(claims.sub, kv);
  const aad = new TextEncoder().encode(`kanjo/pii/v1|tin|${claims.sub}`);
  const env = b64uDecode(b64u);
  return new TextDecoder().decode(await decrypt(piiKey, env, aad));
}

async function getClaimsSafe() {
  const { currentClaims } = await import('../auth/cognitoClient.js');
  return currentClaims();
}

/**
 * UI helper: decrypt only enough of the TIN to show "•••••6789".
 * Returns null on missing or unreadable ciphertext — never throws.
 *
 * Per §10.3 security requirement: "only last 4 digits shown in UI".
 */
export async function tinLastFour(taxInfoRow) {
  if (!taxInfoRow?.tinCiphertextB64u) return null;
  try {
    const plain = await decryptTin(taxInfoRow.tinCiphertextB64u);
    const digits = plain.replace(/\D/g, '');
    if (digits.length < 4) return null;
    return digits.slice(-4);
  } catch {
    return null;
  }
}

/** UI helper: masked TIN (e.g. "•••••6789") with no other digits exposed. */
export async function tinMasked(taxInfoRow) {
  const last4 = await tinLastFour(taxInfoRow);
  return last4 ? `•••••${last4}` : null;
}

function validateTaxYear(y) {
  if (!Number.isInteger(y) || y < 2000 || y > 2200) throw new Error('1099: taxYear must be a year between 2000 and 2200');
}

/** Set or clear the 1099 flag for a vendor in a given tax year. */
export async function setNeeds1099(vendorId, taxYear, flag) {
  validateTaxYear(taxYear);
  const userId = await requireUserId();
  const vendor = await db.vendors.get(vendorId);
  if (!vendor || vendor.userId !== userId) throw new Error('1099: vendor not found');
  const existing = await db.vendorTaxInfo
    .where('[vendorId+taxYear]').equals([vendorId, taxYear])
    .first();
  const row = {
    taxInfoId: existing?.taxInfoId || crypto.randomUUID(),
    userId,
    vendorId,
    taxYear,
    needs1099: flag ? 1 : 0,
    tinCiphertextB64u: existing?.tinCiphertextB64u,
    totalPaidYtdCents: existing?.totalPaidYtdCents || 0,
    lastRecomputedAt: existing?.lastRecomputedAt || 0,
  };
  await db.vendorTaxInfo.put(row);
  return row;
}

/** Set (overwrite) the TIN for a vendor / year. */
export async function setTin(vendorId, taxYear, tinPlain) {
  validateTaxYear(taxYear);
  if (typeof tinPlain !== 'string' || tinPlain.length === 0) throw new Error('tin: empty');
  // Accept 9-digit SSN/EIN with or without dashes.
  if (!/^\d{2}-?\d{7}$|^\d{3}-?\d{2}-?\d{4}$/.test(tinPlain)) throw new Error('tin: format invalid (expected 9 digits)');
  const userId = await requireUserId();
  const existing = await db.vendorTaxInfo.where('[vendorId+taxYear]').equals([vendorId, taxYear]).first();
  const row = {
    taxInfoId: existing?.taxInfoId || crypto.randomUUID(),
    userId,
    vendorId,
    taxYear,
    needs1099: existing?.needs1099 ?? 1,
    tinCiphertextB64u: await encryptTin(tinPlain),
    totalPaidYtdCents: existing?.totalPaidYtdCents || 0,
    lastRecomputedAt: existing?.lastRecomputedAt || 0,
  };
  await db.vendorTaxInfo.put(row);
}

/**
 * Recompute YTD paid totals for the supplied taxYear. Invoices count when:
 *   - status ∈ {CONFIRMED, PAID_LOCAL}
 *   - issuedDate falls inside the calendar year
 *   - isDuplicate is not set
 *
 * Returns all vendors with their YTD totals so callers can show "near threshold" rows.
 */
export async function recomputeYearTotals(taxYear) {
  const userId = await requireUserId();
  if (!Number.isInteger(taxYear) || taxYear < 2000 || taxYear > 2200) throw new Error('1099: invalid taxYear');
  const yearStart = `${taxYear}-01-01`;
  const yearEnd   = `${taxYear}-12-31`;

  const invoices = await db.invoices.where('userId').equals(userId)
    .filter((i) => !i.isDuplicate && (i.status === 'CONFIRMED' || i.status === 'PAID_LOCAL') && i.issuedDate >= yearStart && i.issuedDate <= yearEnd)
    .toArray();

  const totals = new Map();
  for (const inv of invoices) {
    if (!inv.vendorId) continue;
    totals.set(inv.vendorId, (totals.get(inv.vendorId) || 0) + (inv.totalCents || 0));
  }

  // Upsert vendorTaxInfo rows.
  const now = Date.now();
  const out = [];
  for (const [vendorId, totalCents] of totals) {
    const existing = await db.vendorTaxInfo.where('[vendorId+taxYear]').equals([vendorId, taxYear]).first();
    const row = {
      taxInfoId: existing?.taxInfoId || crypto.randomUUID(),
      userId,
      vendorId,
      taxYear,
      needs1099: existing?.needs1099 ?? (totalCents >= THRESHOLD_CENTS ? 1 : 0),
      tinCiphertextB64u: existing?.tinCiphertextB64u,
      totalPaidYtdCents: totalCents,
      lastRecomputedAt: now,
    };
    await db.vendorTaxInfo.put(row);
    out.push(row);
  }
  return out;
}

/** List vendors that crossed the threshold and need a 1099 in `taxYear`. */
export async function listFilingRequired(taxYear) {
  const userId = await requireUserId();
  const rows = await db.vendorTaxInfo.where('[userId+taxYear]').equals([userId, taxYear]).toArray();
  const filtered = rows.filter((r) => r.needs1099 === 1 && r.totalPaidYtdCents >= THRESHOLD_CENTS);
  // Attach vendor name for display.
  return Promise.all(filtered.map(async (r) => ({
    ...r,
    vendor: await db.vendors.get(r.vendorId),
    hasTin: !!r.tinCiphertextB64u,
  })));
}

/** Detect newly-crossed-threshold vendors so the UI can prompt for TIN. */
export async function detectThresholdCrossings(taxYear) {
  const rows = await recomputeYearTotals(taxYear);
  return rows.filter((r) => r.totalPaidYtdCents >= THRESHOLD_CENTS && !r.tinCiphertextB64u);
}

/**
 * Build year-end CSV (vendor name, TIN, total paid). Decrypts TIN values on demand.
 * Per US-4.7 the CSV is the export deliverable, not the e-filing.
 */
export async function exportYearCsv(taxYear) {
  const rows = await listFilingRequired(taxYear);
  const lines = ['vendor_name,tin,total_paid_usd'];
  for (const r of rows) {
    const vendorName = r.vendor?.name || '';
    const total = ((r.totalPaidYtdCents || 0) / 100).toFixed(2);
    let tin = '';
    if (r.tinCiphertextB64u) {
      try { tin = await decryptTin(r.tinCiphertextB64u); }
      catch { tin = ''; }
    }
    lines.push([csvField(vendorName), csvField(tin), total].join(','));
  }
  return lines.join('\n');
}

function csvField(s) {
  if (s == null) return '';
  // Guard against CSV injection: prefix fields starting with a formula trigger with a single quote.
  let str = String(s);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}
